import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { LinearProgress } from '@mui/material'
import { ArrowRightIcon } from '@mui/x-date-pickers'
import AvatarComponent from 'components/avatar'
import BackComponent from 'components/back'
import CalendarUpdateComponent from 'components/calendar-update'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CarpoolOptionProps, CarpoolState, getInitialSelectedDatesState } from 'types/CalendarTypes'
import { MessageProps } from 'types/CommonTypes'
import {
  CarpoolDriverStatus,
  CommunityStudentCarpoolOutputProps,
  DateCarpoolProps,
  DefaultCarpoolType,
} from 'types/CommunityTypes'
import { StudentProps } from 'types/StudentTypes'
import { UserOutputProps, UsersOutputProps } from 'types/UserTypes'
import {
  getCommunityCarpoolTypeLabel,
  getCommunityStudentCarpools,
  updateCommunityStudentCarpoolDetail,
  updateCommunityStudentCarpools,
} from 'utils/communities'
import { getLocalDate, isWeb, titleCaseSchoolNameForUI } from 'utils/constants'
import { getUsers } from 'utils/users'
import './index.css'

// Lightweight shared typography elements for consistent styling
const TxtPrimary: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`text-[15px] font-medium text-[#474747] ${className}`}>{children}</div>
)
const TxtSecondary: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`text-[12px] text-[#797979] ${className}`}>{children}</div>
)
// const TxtMuted: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
//   <div className={`text-xs text-gray-600 ${className}`}>{children}</div>
// )

// Local helper for consistent action links
function ViewLinks({
  showMap,
  showDetails,
  poolType,
  date,
  studentId,
  driverId,
  linkClass = 'text-xs',
  fromPage = 'carpool-tracking',
}: {
  showMap: boolean
  showDetails: boolean
  poolType: string
  date: string
  studentId: string
  driverId: string
  linkClass?: string
  fromPage?: string
}): JSX.Element {
  const navigate = useNavigate()
  return (
    <div className='flex items-center gap-3'>
      {showMap && (
        <button
          role='link'
          aria-label='View live location on map'
          className={`${linkClass} text-gray-700 hover:underline flex items-center gap-1`}
          onClick={() =>
            navigate(`/carpool/live/${poolType}/${date}?id=${studentId}`, { state: { userId: driverId, fromPage } })
          }
        >
          View on map <ArrowRightIcon fontSize='small' className='text-disable' />
        </button>
      )}
      {showDetails && (
        <button
          role='link'
          aria-label='View carpool details'
          className={`${linkClass} text-gray-700 hover:underline flex items-center gap-1`}
          onClick={() =>
            navigate(`/carpool/detail/${poolType}/${date}?id=${studentId}`, { state: { userId: driverId, fromPage } })
          }
        >
          View details <ArrowRightIcon fontSize='small' className='text-disable' />
        </button>
      )}
    </div>
  )
}

const CarpoolTrackingComponent: React.FC<{
  studentId: string
  user: UserOutputProps
  userStudents: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}> = ({ studentId: myStudentId, user, userStudents, onUpdate, failedAvatarUrls, onFailedAvatarUrl }) => {
  // 1. State Hooks
  const navigate = useNavigate()
  const [selectedCarpoolType, setSelectedCarpoolType] = useState<string>(DefaultCarpoolType.DISMISSAL)
  const [communityUsers, setCommunityUsers] = useState<UsersOutputProps[] | undefined>(undefined)
  const [communityStudentCarpool, setCommunityStudentCarpool] = useState<CommunityStudentCarpoolOutputProps>()
  const [isLocationOn, setIsLocationOn] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<string>('')
  const [msg, setMsg] = useState<MessageProps>()

  // 2. Derived Constants
  // Local helpers for time
  const today = getLocalDate()
  const isWeekend: boolean = [0, 6].includes(new Date().getDay())

  // User and student details
  const myUserId: string = user.phoneNumber
  const student: StudentProps | undefined = userStudents.find((s: StudentProps) => s.studentId === myStudentId)
  const {
    schoolName: comId,
    isCarpoolManagementActive = false,
    isDismissalManagementActive = false,
    isSchoolSubscription: isSchoolSubscriptionActive = false,
    isStudentSubscription: isStudentSubscriptionActive = false,
  } = student ?? {}

  // Get carpools based on selected carpool type
  const carpoolsKey = selectedCarpoolType === DefaultCarpoolType.ARRIVAL ? 'arrivalCarpools' : 'dismissalCarpools'
  const carpools: DateCarpoolProps = student?.[carpoolsKey] ?? {}
  const { time, driverId } = carpools[today] ?? {}
  const hasPassengerToday: boolean = time && time > 0 && driverId ? true : false
  const isUserDriverToday: boolean = hasPassengerToday && driverId === myUserId

  // Features and subscriptions
  const isSubscriptionActive: boolean = isSchoolSubscriptionActive || isStudentSubscriptionActive
  const isDismissalManagementEnabled: boolean =
    isDismissalManagementActive && isSchoolSubscriptionActive && selectedCarpoolType === DefaultCarpoolType.DISMISSAL
  const isCarpoolTrackingEnabled: boolean =
    (isCarpoolManagementActive && isSubscriptionActive) || (isDismissalManagementActive && isSchoolSubscriptionActive)

  // Trigger for re-fetching carpool detail when status changes
  const [refreshKey, setRefreshKey] = useState(0)

  // 3. Effect Hooks
  useEffect(() => {
    if (!comId) return

    const loadData = async () => {
      setIsLoading('Loading users and carpool details...')
      const [usersRes, poolRes] = await Promise.all([
        getUsers(comId),
        getCommunityStudentCarpools(comId, myStudentId, {
          poolType: selectedCarpoolType,
          poolDriverId: driverId,
          poolDate: today,
        }),
      ])
      if (usersRes.data) {
        setCommunityUsers((usersRes.data.users as UsersOutputProps[]) ?? [])
      }
      if (poolRes.data?.carpools?.length) {
        setCommunityStudentCarpool(poolRes.data.carpools[0] as CommunityStudentCarpoolOutputProps)
      }
      setIsLoading('')
    }

    loadData()
  }, [comId, myStudentId, selectedCarpoolType, today, driverId, refreshKey])

  // 4. Derived State, Validations and Early Returns
  if (!comId || !communityUsers) return <LinearProgress />

  // Local helpers for driver status
  const driverName = communityUsers?.find((user) => user.userId === driverId)?.userName ?? 'Name Not Found'
  const driverPhotoLink = communityUsers?.find((user) => user.userId === driverId)?.userPhotoLink ?? ''
  const { carpoolDriverStatus } = communityStudentCarpool ?? {}

  // Local helpers for driver status
  const carpoolDriverStatusOrder: CarpoolDriverStatus[] = [
    CarpoolDriverStatus.DESIGNATED,
    CarpoolDriverStatus.EN_ROUTE,
    ...(isDismissalManagementEnabled ? [CarpoolDriverStatus.ARRIVED] : []),
    CarpoolDriverStatus.PICKED_UP,
    CarpoolDriverStatus.DROPPED_OFF,
    CarpoolDriverStatus.COMPLETED,
  ]
  const rank = (s: CarpoolDriverStatus) => carpoolDriverStatusOrder.indexOf(s)
  const isOn = (s: CarpoolDriverStatus) => !!carpoolDriverStatus && rank(carpoolDriverStatus) >= rank(s)

  const selectedEvent: { eventDate: string; eventData: Record<string, unknown> } = {
    eventDate: today,
    eventData: {
      isCarpoolRequest: false,
      carpoolTime: time,
      carpoolDriver: {
        userId: driverId,
        userName: driverName,
        isCurrentUser: driverId === myUserId,
      },
    },
  }
  const options: CarpoolOptionProps = {
    studentId: myStudentId,
    user,
    userStudents,
    poolType: selectedCarpoolType,
    communityUsers: communityUsers ?? [],
  }
  const selectedDatesByState = getInitialSelectedDatesState()
  if (time && driverId) {
    if (driverId === myUserId) {
      selectedDatesByState[CarpoolState.YOU_ARE_DRIVING] = [today]
    } else {
      selectedDatesByState[CarpoolState.SOMEONE_ELSE_DRIVING] = [today]
    }
  } else {
    selectedDatesByState[CarpoolState.NOT_CARPOOLING] = [today]
  }

  if (isLoading) return <LinearProgress />

  // 5. Event Handlers
  const handleSetCustomSchedule = async (carpools: DateCarpoolProps): Promise<any> => {
    setIsLoading(`Updating carpool...`)
    const { msg, error } = await updateCommunityStudentCarpools(comId, myStudentId, {
      carpoolType: selectedCarpoolType,
      carpools,
    })
    if (error) {
      setMsg(msg)
      setIsLoading('')
    } else {
      await onUpdate(() => {
        setMsg({ style: 'success', text: 'Carpool updated successfully.' })
        setIsLoading('')
      })
    }
  }
  const handleStatusUpdate = async (status: CarpoolDriverStatus): Promise<any> => {
    setIsLoading(`Updating carpool driver status...`)
    const { msg, error } = await updateCommunityStudentCarpoolDetail(comId, myStudentId, {
      carpoolType: selectedCarpoolType,
      updateDriverStatus: {
        [today]: status,
      },
    })
    if (error) {
      setMsg(msg)
      setIsLoading('')
    } else {
      await onUpdate(() => {
        setMsg({ style: 'success', text: 'Driver status updated successfully.' })
        setCommunityStudentCarpool((prev) => (prev ? { ...prev, carpoolDriverStatus: status } : prev))
        setRefreshKey((k) => k + 1)
        setIsLoading('')
      })
    }
  }

  const handleStatusOn = async (status: CarpoolDriverStatus): Promise<any> => {
    // Jump forward to this status (or stay put if already higher)
    const currentRank = carpoolDriverStatus ? rank(carpoolDriverStatus) : -1
    const clickedRank = rank(status)
    if (clickedRank > currentRank) {
      await handleStatusUpdate(status)
    }
  }
  const handleStatusOff = async (status: CarpoolDriverStatus): Promise<any> => {
    // Roll back to the immediate previous status if this one is currently ON
    if (!carpoolDriverStatus) return
    const currentRank = rank(carpoolDriverStatus)
    const clickedRank = rank(status)
    if (clickedRank === currentRank) {
      const target = carpoolDriverStatusOrder[Math.max(0, clickedRank - 1)]
      await handleStatusUpdate(target)
    }
  }

  // 6. Render
  // Local reusable action row for driver/passenger status update/display
  const StatusActionRow: React.FC<{
    statusOrAction: string
    actionSubtitle: string
    status: CarpoolDriverStatus
    isDriver: boolean
    onExtra?: React.ReactNode
  }> = ({ statusOrAction, actionSubtitle, status, isDriver, onExtra }) => {
    const completed = isOn(status)
    const current = !completed && !!carpoolDriverStatus && rank(status) === rank(carpoolDriverStatus) + 1
    const dotClass = completed
      ? 'bg-green-600 border-green-600'
      : current
        ? 'bg-custom-yellow border-custom-yellow ring-2 ring-custom-yellow/80 ring-offset-2 ring-offset-white'
        : 'bg-white border-gray-300'

    return (
      <div
        className='relative pl-8 py-3 rounded-md border border-gray-200 p-3 bg-white'
        role='listitem'
        aria-current={current ? 'step' : undefined}
      >
        <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full border ${dotClass}`} />
        <div className='flex items-center justify-between gap-3'>
          <div className='flex flex-col gap-2 flex-1 min-w-0'>
            <TxtPrimary className={current ? 'text-gray-900' : ''}>{statusOrAction}</TxtPrimary>
            {isDriver && <TxtSecondary>{actionSubtitle}</TxtSecondary>}
            {onExtra}
          </div>
          {isDriver ? (
            <button
              aria-label={
                completed ? `Turn off ${statusOrAction.toLowerCase()}` : `Turn on ${statusOrAction.toLowerCase()}`
              }
              className={`px-3.5 py-1.5 min-h-[36px] text-sm rounded-md border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-custom-yellow shrink-0 ${
                completed
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => (completed ? handleStatusOff(status) : handleStatusOn(status))}
            >
              {completed ? 'On' : 'Off'}
            </button>
          ) : (
            <div aria-label={completed ? 'Completed' : 'Pending'} className='flex items-center'>
              {completed ? (
                <CheckCircleRoundedIcon className='text-green-600' fontSize='small' />
              ) : (
                <CancelRoundedIcon className='text-gray-300' fontSize='small' />
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
  const body = (
    <div className={isWeb ? 'm-1' : 'flex p-4 px-5 flex-col items-start gap-3 flex-[1_0_0] self-stretch'}>
      {/* Carpool Types */}
      <div className='w-full'>
        <div className='flex justify-between items-start gap-2'>
          <div className='flex flex-col'>
            <h3 className='text-headings font-medium mb-0.5'>Your Carpool</h3>
          </div>
          <div className='flex flex-col items-center gap-1 flex-shrink-0 pl-2'>
            <div className='flex gap-1.5'>
              {Object.values(DefaultCarpoolType).map((type) => {
                const label = getCommunityCarpoolTypeLabel(type)
                const isSelected = type === selectedCarpoolType
                return (
                  <button
                    key={type}
                    onClick={() => setSelectedCarpoolType(type)}
                    title={`Switch to ${label}`}
                    className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                      isSelected
                        ? 'bg-custom-yellow border-custom-yellow text-gray-900 font-medium'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`carpool-type-${type}`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Carpool Status and Actions */}
      {isWeekend ? (
        <div className='w-full mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-700'>
          <div className='flex items-start gap-2'>
            <CalendarMonthOutlinedIcon className='text-gray-400 mt-0.5' sx={{ fontSize: 16 }} />
            <div className='flex flex-col gap-1.5'>
              <div className='text-sm font-medium'>No carpool on weekends.</div>
              <div className='text-xs text-gray-600'>Resume on school days.</div>
            </div>
          </div>
        </div>
      ) : communityStudentCarpool && hasPassengerToday ? (
        <div className='w-full mt-2 rounded-lg border border-gray-200 bg-white p-3'>
          <div className='flex items-center justify-between mb-2'>
            <div className='text-sm font-medium text-gray-800'>Today</div>
            {driverId && (
              <div className='flex items-center gap-3'>
                <ViewLinks
                  showMap={!!isLocationOn}
                  showDetails={!isUserDriverToday}
                  poolType={selectedCarpoolType}
                  date={today}
                  studentId={myStudentId}
                  driverId={driverId || ''}
                />
                {isUserDriverToday && (
                  <button
                    role='link'
                    aria-label='View carpool schedule and details'
                    className='text-xs text-gray-700 hover:underline flex items-center gap-1'
                    onClick={() =>
                      navigate(`/carpool/detail/${selectedCarpoolType}/${today}?id=${myStudentId}`, {
                        state: { userId: driverId, fromPage: 'carpool-tracking' },
                      })
                    }
                  >
                    {(() => {
                      const date = new Date(communityStudentCarpool.carpoolScheduledAt || today)
                      const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        ...(communityStudentCarpool.carpoolScheduledAt && {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        }),
                        timeZone: 'UTC',
                      })
                      return dateTimeFormat.format(date)
                    })()}
                    <ArrowRightIcon fontSize='small' className='text-disable' />
                  </button>
                )}
              </div>
            )}
          </div>
          {isUserDriverToday ? (
            <div className='text-xs text-gray-600'>{`You're the driver today. Update your status below.`}</div>
          ) : (
            <div className='flex items-center gap-2'>
              <AvatarComponent
                size={6}
                name={driverName || 'Driver'}
                photoLink={driverPhotoLink}
                failedPhotoLinks={failedAvatarUrls}
                onFailedPhotoLink={onFailedAvatarUrl}
              />
              <div className='flex flex-col w-full'>
                <div className='flex items-center justify-between'>
                  <TxtPrimary>Driver: {driverName || 'TBD'}</TxtPrimary>
                  {/* Details link shown in header to avoid duplication */}
                </div>
                <TxtSecondary className='mt-0.5'>
                  {(() => {
                    const date = new Date(communityStudentCarpool.carpoolScheduledAt || today)
                    const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      ...(communityStudentCarpool.carpoolScheduledAt && {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      }),
                      timeZone: 'UTC',
                    })
                    return dateTimeFormat.format(date)
                  })()}
                </TxtSecondary>
                <TxtSecondary className='mt-1'>
                  You have a carpool today. Only the assigned driver can update status. Others can view updates.
                </TxtSecondary>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className='w-full mt-2' aria-live='polite' role='status'>
          <div className='rounded-lg border border-gray-200 bg-[#FAFAFA] p-3 flex items-start gap-2'>
            <CalendarMonthOutlinedIcon fontSize='small' className='text-gray-400 mt-0.5' />
            <div className='flex-1'>
              <TxtPrimary>Not carpooling today</TxtPrimary>
              <TxtSecondary>If your plans have changed, update schedule below.</TxtSecondary>
            </div>
          </div>
          <div className='mt-2'>
            <CalendarUpdateComponent
              selectedDatesByState={selectedDatesByState as Record<CarpoolState, string[]>}
              selectedEvents={[selectedEvent]}
              carpoolOptions={options}
              onSave={handleSetCustomSchedule}
              variant='compact'
            />
          </div>
        </div>
      )}

      {/* Driver Actions */}
      {isCarpoolTrackingEnabled &&
        (isUserDriverToday || hasPassengerToday) &&
        carpoolDriverStatus &&
        Object.values(CarpoolDriverStatus).includes(carpoolDriverStatus) && (
          <div className='w-full mt-2 rounded-lg border border-gray-200 bg-white p-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <h4 className='text-base font-semibold text-gray-900 m-0'>Driver Actions</h4>
                <span className='text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600'>Beta</span>
              </div>
            </div>

            <div className='mt-2 flex flex-col gap-1.5'>
              {/* 1. Live Location Sharing */}
              <div className='relative pl-8 flex items-center justify-between gap-3 rounded-md border border-gray-200 p-2 bg-gray-50'>
                <div className='flex flex-col gap-2 flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <TxtPrimary>Share live location</TxtPrimary>
                    <span className='text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600'>
                      Coming soon
                    </span>
                  </div>
                  {isUserDriverToday && (
                    <TxtSecondary>Parents in this carpool can view your live location</TxtSecondary>
                  )}
                  {isLocationOn && (
                    <div className='text-[11px] mt-1'>
                      <ViewLinks
                        showMap={true}
                        showDetails={!!hasPassengerToday}
                        poolType={selectedCarpoolType}
                        date={today}
                        studentId={myStudentId}
                        driverId={driverId || ''}
                        linkClass='text-[11px]'
                      />
                    </div>
                  )}
                </div>
                {isUserDriverToday ? (
                  <button
                    aria-label={isLocationOn ? 'Turn off location sharing' : 'Turn on location sharing'}
                    className={`px-3.5 py-1.5 min-h-[36px] text-sm rounded-md border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-custom-yellow shrink-0 ${
                      isLocationOn
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                    disabled={true}
                    onClick={() => {
                      const next = !isLocationOn
                      setIsLocationOn(next)
                      setMsg({
                        style: 'success',
                        text: next ? 'Location tracking enabled' : 'Location tracking disabled',
                      })
                    }}
                  >
                    {isLocationOn ? 'On' : 'Off'}
                  </button>
                ) : (
                  <div
                    aria-label={isLocationOn ? 'Location sharing on' : 'Location sharing off'}
                    className='flex items-center'
                  >
                    {isLocationOn ? (
                      <CheckCircleRoundedIcon className='text-green-600' fontSize='small' />
                    ) : (
                      <CancelRoundedIcon className='text-gray-300' fontSize='small' />
                    )}
                  </div>
                )}
              </div>

              {/* Chronological steps */}
              <div
                className='relative flex flex-col gap-2 before:content-[""] before:absolute before:left-3 before:top-0 before:bottom-0 before:w-px before:bg-gray-200'
                role='list'
              >
                {/* 2. Enroute to pickup */}
                <StatusActionRow
                  statusOrAction={isUserDriverToday ? 'Enroute to pickup' : 'Enroute'}
                  actionSubtitle={`Let ${isDismissalManagementEnabled ? 'school and' : ''} parents know you’re on the way`}
                  status={CarpoolDriverStatus.EN_ROUTE}
                  isDriver={isUserDriverToday}
                />

                {/* 3. Announce arrival at school */}
                {isDismissalManagementEnabled && (
                  <StatusActionRow
                    statusOrAction={isUserDriverToday ? 'Announce arrival at school' : 'Arrived at school'}
                    actionSubtitle='Notify staff on carpool lane'
                    status={CarpoolDriverStatus.ARRIVED}
                    isDriver={isUserDriverToday}
                    onExtra={
                      isOn(CarpoolDriverStatus.ARRIVED) ? (
                        <div className='mt-1'>
                          <span className='text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-mono'>
                            Code: {`${(driverId || '').slice(-4) || '0000'}-${dayjs(today).format('DDHH')}`}
                          </span>
                        </div>
                      ) : undefined
                    }
                  />
                )}

                {/* 4. Announce pickup */}
                <StatusActionRow
                  statusOrAction={isUserDriverToday ? 'Announce pickup' : 'Pickup status'}
                  actionSubtitle='Notify parents when riders are onboard'
                  status={CarpoolDriverStatus.PICKED_UP}
                  isDriver={isUserDriverToday}
                />

                {/* 5. Announce arrival at dropoff */}
                <StatusActionRow
                  statusOrAction={isUserDriverToday ? 'Announce arrival at dropoff' : 'Drop off status'}
                  actionSubtitle='Let parents know riders are dropped off'
                  status={CarpoolDriverStatus.DROPPED_OFF}
                  isDriver={isUserDriverToday}
                />
              </div>
            </div>
          </div>
        )}

      {/* Feature not enabled info */}
      {!isCarpoolTrackingEnabled && (
        <div className='w-full mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-gray-700'>
          <div className='text-sm font-medium text-gray-800'>Carpool Management not enabled</div>
          <div className='text-xs text-gray-600'>
            Your school subscription doesn&apos;t include carpool management or it&apos;s disabled.
          </div>
        </div>
      )}
    </div>
  )
  const rightElement = (
    <div className='grow-0 shrink min-w-0 max-w-[35%]'>
      <span
        className='inline-block max-w-full truncate text-xs text-gray-600 px-1 py-0.5 bg-gray-50 rounded'
        title={titleCaseSchoolNameForUI(comId)}
      >
        {titleCaseSchoolNameForUI(comId)}
      </span>
    </div>
  )

  return (
    <>
      {msg && <ToastComponent {...msg} onClose={() => setMsg(undefined)} />}

      {!isWeb ? (
        <div className='flex flex-col h-screen'>
          <BackComponent text="Track Today's Pickup" rightElement={rightElement} />
          {body}
        </div>
      ) : (
        <WizardComponent
          title="Track Today's Pickup"
          data_testId='modal-dialog-action-submit'
          rightElement={rightElement}
        >
          {body}
        </WizardComponent>
      )}
    </>
  )
}

function Index({
  user,
  students,
  onUpdate,
  failedAvatarUrls,
  onFailedAvatarUrl,
}: {
  user?: UserOutputProps
  students: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}): JSX.Element {
  const [searchParams] = useSearchParams()

  // Build props for child component
  const studentId = searchParams.get('id') ?? ''
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)

  if (!user || !studentId || !student) return <LinearProgress />
  return (
    <CarpoolTrackingComponent
      studentId={studentId}
      user={user}
      userStudents={students}
      onUpdate={onUpdate}
      failedAvatarUrls={failedAvatarUrls}
      onFailedAvatarUrl={onFailedAvatarUrl}
    />
  )
}

export default Index
